/**
 * Translations (ARCHITECT D4: English now, i18n-ready). Every user-visible string is a key.
 * Each frontend module ships its own namespace (`modules/<name>/src/i18n/en.json`);
 * `common` holds shared words (buttons, errors, table labels).
 */

import i18next, { type i18n as I18n, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";

import commonEn from "./locales/en/common.json";
import commonGu from "./locales/gu/common.json";
import commonHi from "./locales/hi/common.json";
import commonMr from "./locales/mr/common.json";

export { I18nextProvider, Trans, useTranslation } from "react-i18next";
export type { I18n };

export interface Language {
  code: string;
  /** Name in its own script, as shown in the language switcher. */
  label: string;
  enabled: boolean;
}

/**
 * Languages of the admin and vendor panels' switcher. Hindi, Marathi and Gujarati are translated for the storefront
 * (P6-13, `apps/storefront/src/lib/locale.ts`) and the shared `common` strings; the panels' module screens are English
 * only so far, so they stay off here until those are translated.
 */
export const LANGUAGES: Language[] = [
  { code: "en", label: "English", enabled: true },
  { code: "hi", label: "हिन्दी", enabled: false },
  { code: "mr", label: "मराठी", enabled: false },
  { code: "gu", label: "ગુજરાતી", enabled: false },
];

export const DEFAULT_LANGUAGE = "en";

/** Namespaced resources from modules: { en: { accounts: {...}, core: {...} } }. */
export type ModuleResources = Record<string, Record<string, unknown>>;

export function createI18n(
  moduleResources: ModuleResources[] = [],
  language = DEFAULT_LANGUAGE,
  /** Languages this app offers (the storefront offers more than the panels' switcher). */
  allowed: readonly string[] = LANGUAGES.filter((l) => l.enabled).map((l) => l.code),
): I18n {
  const resources: Resource = {
    en: { common: commonEn },
    hi: { common: commonHi },
    mr: { common: commonMr },
    gu: { common: commonGu },
  };
  for (const bundle of moduleResources) {
    for (const [lng, namespaces] of Object.entries(bundle)) {
      resources[lng] = { ...(resources[lng] ?? {}), ...(namespaces as Resource[string]) };
    }
  }
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng: allowed.includes(language) ? language : DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: "common",
    ns: Object.keys(resources[DEFAULT_LANGUAGE] ?? {}),
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false,
    initAsync: false,
  });
  return instance;
}
